/* eslint-disable complexity */

'use client'

import { Box } from '@mui/material'
import { Image, ImageData } from '@media-components'
import {
  Accordion,
  ActionButton,
  ActionButtonData,
  FeatureCard,
  FeatureCardData,
  SectionHeader,
  SectionHeaderData,
  SectionWrapper,
  type SectionHeaderProps,
  type SectionWrapperProps,
} from '@components/common'
import { getStyles, type CustomStyles } from '@utils/styles'
import defaultStyles, { type StyleClassNames } from './styles'

export type FeatureSectionData = {
  sectionHeaderData?: SectionHeaderData
  imageData?: ImageData
  featureCardData?: FeatureCardData[]
  cardPerRow?: 'primary' | 'secondary'
  darkBg?: boolean
  showSearchBar?: boolean
  searchPlaceholder?: string
}

export type FeatureSectionProps = FeatureSectionData & {
  actionButtonData?: ActionButtonData
  needDividerForAccordion?: boolean
  accordionView?: boolean
  isFreeItConsultPage?: boolean
  needImage?: boolean
  featureCardWrapperSx?: CustomStyles<StyleClassNames>
  sectionHeaderProps?: Partial<SectionHeaderProps>
  withShadow?: boolean
} & Partial<SectionWrapperProps>

export function FeatureSection({
  sectionHeaderData,
  featureCardData = [],
  cardPerRow,
  actionButtonData,
  imageData,
  accordionView = false,
  darkBg = false,
  showDivider,
  compact = true,
  csx,
  isFreeItConsultPage = false,
  needImage = false,
  needDividerForAccordion = false,
  featureCardWrapperSx,
  sectionHeaderProps,
  withShadow = false,
}: FeatureSectionProps) {
  const styles = getStyles(defaultStyles, featureCardWrapperSx)

  const accordionData = accordionView
    ? featureCardData.map(({ title, description, documentData }) => ({
        title,
        description,
        simpleDocumentRendererData: documentData,
      }))
    : undefined

  const getFeatureCardStyles = (num: number) => {
    const isPrimaryVariant = cardPerRow === 'primary'
    const flexBasis = {
      xs: '100%',
      sm: 'calc(50% - 12px)',
      md: isPrimaryVariant ? 'calc(33% - 18px)' : 'calc(50% - 12px)',
    }
    if (isPrimaryVariant && num === 4) {
      flexBasis.md = 'calc(50% - 12px)'
    }
    return { flexBasis }
  }

  return (
    <SectionWrapper
      flexCol={{ xs: '20px', md: '32px' }}
      compact={compact}
      alternateBg={darkBg}
      showDivider={showDivider}
      csx={csx}
    >
      {sectionHeaderData && (
        <SectionHeader
          {...sectionHeaderData}
          csx={{ infoWrapper: { width: '100%' } }}
          darkBg={darkBg}
          headingComponentVariant="h2"
          buttonVariant="outlined"
          {...sectionHeaderProps}
        />
      )}

      {needImage ||
        (!accordionData &&
          !accordionView &&
          imageData &&
          !isFreeItConsultPage && (
            <Image {...imageData} {...styles('image')} />
          ))}

      <Box
        {...styles('accordionSectionWrapper', {
          maxHeight: accordionView && accordionData ? '504px' : 'unset',
        })}
      >
        {accordionView && accordionData ? (
          <Accordion
            accordionData={accordionData}
            csx={{
              wrapper: styles('accordionWrapper', {
                maxWidth: imageData ? '668px' : '100%',
              }).sx,
              accordionWrapper: styles('accordion').sx,
            }}
            openInitial
            needDivider={needDividerForAccordion}
          />
        ) : (
          <Box {...styles('cardWrapper')}>
            {/* {isFreeItConsultPage
              ? 
              : featureCardData.map((card, idx) => (
                  <BusinessCard
                    idx={idx}
                    key={idx}
                    {...card}
                    csx={{
                      wrapper: styles(['featureCardWrapper', 'businessCard'])
                        .sx,
                      documentWrapper: styles('documentWrapper').sx,
                    }}
                  />
                ))} */}

            {featureCardData.map((item, idx) => (
              <FeatureCard
                key={idx}
                {...item}
                {...styles(
                  'featureCardWrapper',
                  getFeatureCardStyles(featureCardData.length)
                )}
                animation="flip"
                dark={darkBg}
                titleVariant="body1.600"
                iconImageSx={{
                  backgroundColor: darkBg ? '#ffffff10' : '#00000005',
                }}
                {...(withShadow && {
                  csx: {
                    wrapper: {
                      // boxShadow: 3,
                      border: '1px solid',
                      borderColor: 'border.dark',
                    },
                    // title: {
                    //   fontSize: '1rem !important',
                    //   fontWeight: '600 !important',
                    // },
                  },
                })}
              />
            ))}
          </Box>
        )}
        {accordionView && accordionData && imageData && (
          <Image {...imageData} {...styles('accordionImage')} />
        )}
      </Box>

      {actionButtonData && (
        <ActionButton
          {...actionButtonData}
          csx={{ button: styles('actionButton').sx }}
          showArrow
          variant="contained"
        />
      )}
    </SectionWrapper>
  )
}
